import React, { Component, RefObject } from 'react';
import {
  calculateEtaWithAlgorithm,
  deleteDraftBundle,
  pickupOnTop,
  publishDraft,
  removeInstructionsFromBundle,
  reorderBundleWithAlgorithm,
  reorderDraftNew,
  reverseDeliveries,
  splitBundleAndCreateNew,
} from '../utils/bundleDraft';
import './PlannerBundle.scss';
import {
  BundleJson,
  BundleStatus,
  Instruction,
  InstructionType,
  ReorderMode,
  TravelMode,
} from '../../../types/bundleTypes';
import { fetchOrdersForMonitor } from 'fetch/orderFetch';
import { OrderMonitor, Region } from '../../../types/orderTypes';
import Swal, { SweetAlertResult } from 'sweetalert2';
import Fetch from '../../../utils/fetch';
import { settings } from '../../../settings';
import ChangeShelfModal from '../modals/ChangeShelfModal';
import {
  OrderInstruction,
  toOrderInstructions,
} from '../../../components/maps/BundleMap';
import ETAOptionsModal from '../modals/ETAOptionsModal';
import { findNextInstruction } from '../../bundles/components/BundleUtils';
import { Loader } from '../../../components/Loader/Loader';
import dayjs, { Dayjs } from 'dayjs';
import {
  OrderBundle,
  OrderBundleColumn,
} from 'components/OrderBundle/OrderBundle';
import { PlannerBundleMenus } from './PlannerBundleMenus';

interface Props {
  bundle: BundleJson;
  expanded: boolean;
  bundleDraftIsSelected: boolean;
  mapOnTop?: boolean;
  onSavedModification: () => void;
  onOrdersRemoved: (bundleId: string, orderIds: string[]) => void;
  onSelectBundleDraft?: (id: string) => void;
  onDeselectBundleDraft?: (id: string) => void;
  onExpand?: (bundle: BundleJson, expanded: boolean) => void;
  onMapSelected?: (bundle: BundleJson) => void;
  region: Region;
  monochrome?: boolean;
  monitorView?: boolean;
  completedStatusList?: boolean;
  clearUpdateQueue?: (bundleId: string) => void;
  listOfBundlesToUpdate?: string[];
  fetchCurrentStatus?: (
    statusOfBundle: { [bundleId: string]: string },
    reSort?: boolean
  ) => void;
  setZonesOfBundles?: (newZonesOfBundles: {
    [bundleId: string]: string;
  }) => void;
  autoSort?: boolean;
  openModalToSelectBundle?: (bundleId: string) => void;
  selectedDate?: Dayjs;
  itemColumns: Array<keyof typeof OrderBundleColumn>;
  useItemBorder?: boolean;
}

interface State {
  bundle: BundleJson;
  orderInstructions: OrderInstruction[];
  // Modals
  showChangeShelfModal: boolean;
  showTravelModeETAModal: boolean;
  // View changes
  recreateModeActive: boolean;
  movingToBundleActive: boolean;
  // Actions in progress
  actionInProgress: boolean;
  unsavedChanges: boolean;
  selectedOrders: string[];
  warningUpdateModalOpen: boolean;
  isBundleUpdating: boolean;
  reorderHoverShift: boolean;
}

interface InstructionGroup {
  index: number;
  instruction: Instruction;
  address: string;
  count: number;
}

export default class PlannerBundle extends Component<Props, State> {
  private readonly plannerBundleRef: RefObject<HTMLDivElement>;
  private statusColorTimer: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      bundle: props.bundle,
      orderInstructions: [],
      actionInProgress: false,
      showChangeShelfModal: false,
      showTravelModeETAModal: false,
      recreateModeActive: false,
      movingToBundleActive: false,
      unsavedChanges: false,
      selectedOrders: [],
      warningUpdateModalOpen: false,
      isBundleUpdating: false,
      reorderHoverShift: false,
    };
    this.plannerBundleRef = React.createRef();
  }

  componentDidMount = async (): Promise<void> => {
    const { bundle, monitorView, completedStatusList } = this.props;
    let orders: OrderMonitor[];
    try {
      orders = await fetchOrdersForMonitor(
        this.state.bundle.instructions.map(instruction => instruction.orderId)
      );
    } catch {
      orders = [];
    }

    this.setState({
      orderInstructions: toOrderInstructions(bundle, orders),
    });
    if (monitorView && !completedStatusList) {
      this.updateBundleStatusColor();
      this.statusColorTimer = setInterval(
        () => this.updateBundleStatusColor(),
        30_000
      );
    }
  };

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ): void {
    const { bundle, clearUpdateQueue, listOfBundlesToUpdate, monitorView } =
      this.props;
    const {
      recreateModeActive,
      warningUpdateModalOpen,
      isBundleUpdating,
      movingToBundleActive,
    } = this.state;
    const isPusherUpdates = listOfBundlesToUpdate?.includes(bundle.id);

    if (
      (isPusherUpdates && !warningUpdateModalOpen) ||
      JSON.stringify(bundle) !== JSON.stringify(prevProps.bundle)
    ) {
      if (!monitorView) {
        this.updateOrders()
          .then(() => {
            this.setState({ isBundleUpdating: false });
          })
          .catch(error => console.log(error));
      } else if (!isBundleUpdating) {
        this.setState({ isBundleUpdating: true }, () => {
          if (recreateModeActive || movingToBundleActive) {
            this.setState({ warningUpdateModalOpen: true }, () =>
              this.fireUpdateWarning(bundle.id)
            );
          } else {
            this.updateOrders()
              .then(() => {
                clearUpdateQueue(bundle.id);
                this.setState({ isBundleUpdating: false });
              })
              .catch(error => console.log(error));
          }
        });
      }
    }
  }

  componentWillUnmount(): void {
    clearInterval(this.statusColorTimer);
  }

  updateBundleStatusColor(): void {
    const { fetchCurrentStatus, bundle, monitorView, autoSort } = this.props;
    if (
      monitorView &&
      this.plannerBundleRef &&
      this.plannerBundleRef.current &&
      this.plannerBundleRef.current.nextElementSibling
    ) {
      const firstDelivery = bundle.instructions.filter(
        instruction => instruction.type === InstructionType.Delivery
      )[0];

      const now = dayjs();
      if (
        firstDelivery &&
        firstDelivery?.eta?.isValid() &&
        now.diff(firstDelivery?.eta, 'minute') > -20
      ) {
        const nextDelivery = findNextInstruction(
          bundle,
          InstructionType.Delivery
        );

        let newClassName = 'bundles-list-status';
        let status = 'UNKNOWN';
        if (bundle.status === 'COMPLETED') {
          status = 'FINISHED';
          newClassName += ' finished';
        } else if (nextDelivery && nextDelivery.eta) {
          const diff = nextDelivery.eta.diff(now, 'minute');
          if (diff <= -1) {
            if (
              this.plannerBundleRef.current.nextElementSibling.className.includes(
                'delay'
              )
            ) {
              return;
            }
            newClassName += ' delay';
            status = 'DELAY';
          } else {
            if (
              this.plannerBundleRef.current.nextElementSibling.className.includes(
                'on-time'
              )
            ) {
              return;
            }
            newClassName += ' on-time';
            status = 'ON_TIME';
          }
        }
        this.plannerBundleRef.current.nextElementSibling.className =
          newClassName;
        fetchCurrentStatus({ [bundle.id]: status }, autoSort);
      }
    }
  }

  updateOrders = async (): Promise<void> => {
    const { bundle } = this.props;
    const orderIds = bundle.instructions.map(
      instruction => instruction.orderId
    );
    this.updateBundleStatusColor();

    let newOrdersList: OrderMonitor[];
    try {
      newOrdersList = await fetchOrdersForMonitor(orderIds);
    } catch {
      newOrdersList = [];
    }
    this.setState({
      bundle,
      orderInstructions: toOrderInstructions(bundle, newOrdersList),
    });
  };

  fireUpdateWarning = (bundleId: string): Promise<void> =>
    Swal.fire({
      title: 'The Bundle has been updated!',
      text: `Please press ok and the bundle ${bundleId} will automatically refresh. After refresh, you will be able to edit bundle again.`,
      icon: 'warning',
      allowOutsideClick: false,
      confirmButtonText: 'Ok',
      confirmButtonColor: '#d33',
    })
      .then(() => {
        this.updateOrders()
          .then(() => {
            this.props.clearUpdateQueue(bundleId);
            this.setState({
              warningUpdateModalOpen: false,
              isBundleUpdating: false,
            });
          })
          .catch(error => console.error(error.message));
      })
      .catch(error => console.error(error.message));

  removeOrderInstructions = async (...orderIds: string[]): Promise<boolean> => {
    const { bundle } = this.state;
    const status = bundle.status;
    const refreshAfterRemovingOrders = () => {
      this.props.onOrdersRemoved(bundle.id, orderIds);
    };

    if (status === BundleStatus.Draft) {
      this.setState(currentState => {
        return {
          ...currentState,
          orderInstructions: currentState.orderInstructions.filter(
            orderInstruction => !orderIds.includes(orderInstruction.order.id)
          ),
        };
      });
      await removeInstructionsFromBundle(this.state.bundle, orderIds).then(
        () => {
          refreshAfterRemovingOrders();
        },
        () => {
          Swal.fire('Instruction could not be deleted', undefined, 'error')
            .then(() => refreshAfterRemovingOrders())
            .catch(error => console.error(error.message));
        }
      );
      return true;
    } else {
      const result = await Swal.fire({
        title: `Remove order from`,
        html: `Are you sure you want to remove order(s) <b>${orderIds.join(
          ','
        )}</b> from bundle <b>${
          bundle.id
        }</b>?<br/> The order will change its status to Ready.`, // eslint-disable-line max-len
        icon: 'warning',
        showCancelButton: true,
        focusCancel: true,
        confirmButtonText: 'Remove order',
        confirmButtonColor: '#d33',
        cancelButtonText: 'Never mind',
        cancelButtonColor: '#ccc',
      });
      if (result.value) {
        await removeInstructionsFromBundle(this.state.bundle, orderIds);

        this.setState({
          orderInstructions: this.state.orderInstructions.filter(
            orderInstruction => !orderIds.includes(orderInstruction.order.id)
          ),
        });
        refreshAfterRemovingOrders();
        return true;
      }
      return false;
    }
  };

  cancelBundle = async (bundle: BundleJson): Promise<void> => {
    const response = await Fetch.put({
      url: `${settings.newApiUrl}/bundle/${bundle.id}/cancel`,
    });

    if (response.ok) {
      await Swal.fire(
        'Success!',
        `Bundle ${bundle.id} is cancelled`,
        'success'
      );
      await this.props.onSavedModification();
    } else {
      await Swal.fire('Bundle could not be cancelled', undefined, 'error');
    }
  };

  calculateEtaFromAlgorithm = (
    bundleId: string,
    travelMode: TravelMode,
    timePerStop: number
  ): void => {
    this.setState({ actionInProgress: true });
    calculateEtaWithAlgorithm(
      bundleId,
      travelMode,
      timePerStop,
      () => {
        this.setState({ actionInProgress: false });
        this.props.onSavedModification();
      },
      response => {
        this.setState({ actionInProgress: false });
        Swal.fire({
          title: `Something unexpected occurred when calculating ETAs. Error response: ${JSON.stringify(
            response
          )}`,
          icon: 'error',
        }).catch(error => console.error(error.message));
      }
    ).catch(error => console.error(error.message));
  };

  clickMenuCancelBundle = (): void => {
    const { bundle } = this.state;
    this.setState({ actionInProgress: true });

    Swal.fire({
      title: 'Cancel bundle',
      text: `Are you sure you want to cancel bundle ${bundle.id}? This is a irreversible action. All the orders in the bundle will change their status to Ready.`, // eslint-disable-line max-len
      icon: 'warning',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Cancel bundle',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Never mind',
      cancelButtonColor: '#ccc',
    })
      .then(result => {
        if (result.value) {
          this.cancelBundle(bundle).then(() => null);
          this.props.onSavedModification();
        }
        this.setState({ actionInProgress: false });
      })
      .catch(error => console.error(error.message));
  };

  toggleExpanded = (): void => {
    if (this.props.onExpand) {
      this.props.onExpand(this.state.bundle, !this.props.expanded);
    }
  };

  findPickupInstructionsGroup = (
    instructions: OrderInstruction[]
  ): InstructionGroup | null => {
    let group: InstructionGroup = null;

    for (let i = 0, l = instructions.length; i < l; i++) {
      const instr = instructions[i].instruction;
      const order = instructions[i].order;
      if (!order) {
        break;
      }
      const address = order.originAddress.fullAddressString.replace(
        /\s\s+/g,
        ' '
      );

      if (group === null) {
        group = {
          address,
          instruction: instr,
          index: i,
          count: 1,
        };
      } else if (instr.type === InstructionType.Pickup) {
        group.count++;
      } else {
        break;
      }
    }
    return group &&
      group.count > 1 &&
      group.instruction.type === InstructionType.Pickup
      ? group
      : null;
  };

  startReordering = (): void => {
    if (!this.props.expanded) {
      this.toggleExpanded();
    }
    this.setState({
      recreateModeActive: false,
      movingToBundleActive: false,
    });
  };

  stopReordering = (): void => {
    const orders = this.state.orderInstructions.map(
      orderInstruction => orderInstruction.order
    );
    const orderInstructions = toOrderInstructions(this.props.bundle, orders);
    this.setState({
      unsavedChanges: false,
      movingToBundleActive: false,
      orderInstructions,
    });
  };

  stopRecreating = (): void => {
    this.setState({
      selectedOrders: [],
      recreateModeActive: false,
      movingToBundleActive: false,
      unsavedChanges: false,
    });
  };

  startRecreating = (): void => {
    if (!this.props.expanded) {
      this.toggleExpanded();
    }
    this.setState({
      recreateModeActive: true,
      movingToBundleActive: false,
    });
  };

  startMoving = (): void => {
    if (!this.props.expanded) {
      this.toggleExpanded();
    }
    this.setState({
      recreateModeActive: false,
      movingToBundleActive: true,
    });
  };

  stopMoving = (): void => {
    this.setState({
      recreateModeActive: false,
      movingToBundleActive: false,
      selectedOrders: [],
    });
  };

  clearSelectedOrdersAndStopMoving = (): void => {
    this.setState({
      selectedOrders: [],
      recreateModeActive: false,
      movingToBundleActive: false,
    });
  };

  onSelectOrderInstruction = (
    orderInstructions: Array<OrderInstruction>
  ): void => {
    const orders = orderInstructions.map(
      orderInstruction => orderInstruction.order.id
    );
    this.setState({
      selectedOrders: orders,
    });
  };

  onDeleteOrderInstruction = async (orderId: string) => {
    if (this.state.actionInProgress) {
      /* Do nothing when there is another action still in progress */
      return;
    }

    this.setState({
      actionInProgress: true,
    });
    await this.removeOrderInstructions(orderId);
    this.setState({
      actionInProgress: false,
    });
  };

  askForBundleStatus = async (bundleId: string): Promise<SweetAlertResult> =>
    Swal.fire({
      title: `Set status of created bundle`,
      html: `Would you like to set bundle ${bundleId} as <b>Published</b>?`,
      icon: 'info',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#00b4bb',
      cancelButtonText: 'No, leave it as Draft',
      cancelButtonColor: '#55ab6d',
    });

  recreateBundle = (): void => {
    const { selectedOrders } = this.state;
    const { region, bundle } = this.props;
    this.setState({ actionInProgress: true });
    splitBundleAndCreateNew(selectedOrders, region, bundle.id)
      .then(async createdBundle => {
        let fetchedOrders: OrderMonitor[];
        try {
          fetchedOrders = await fetchOrdersForMonitor(
            createdBundle.instructions.map(instruction => instruction.orderId)
          );
        } catch {
          fetchedOrders = [];
        }
        const orderInstructions = toOrderInstructions(
          createdBundle,
          fetchedOrders
        );
        await reorderDraftNew(
          createdBundle.id,
          pickupOnTop(orderInstructions).map(
            orderInstruction => orderInstruction.instruction
          )
        );
        const bundleShouldBePublished = await this.askForBundleStatus(
          createdBundle.id
        );
        if (bundleShouldBePublished.value) {
          await publishDraft(createdBundle.id);
        }
        this.setState(
          {
            actionInProgress: false,
            selectedOrders: [],
            recreateModeActive: false,
            movingToBundleActive: false,
          },
          async () => await this.props.onSavedModification()
        );
      })
      .catch(() => {
        this.setState({
          actionInProgress: false,
          selectedOrders: [],
          recreateModeActive: false,
          movingToBundleActive: false,
        });
      });
  };

  saveActions = async (
    bundle: BundleJson,
    orderInstructions: Array<OrderInstruction>
  ) => {
    this.setState({ actionInProgress: true });
    try {
      await reorderDraftNew(
        bundle.id,
        orderInstructions.map(orderInstruction => orderInstruction.instruction)
      );

      this.setState({
        unsavedChanges: false,
        actionInProgress: false,
      });
      this.props.onSavedModification();
      await this.updateOrders();
      /* Update the default `orderInstructions` to latest data */
      this.setState({
        orderInstructions: [...orderInstructions],
      });
    } catch (error) {
      Swal.fire('Error reordering', (error as Error).message, 'error');
      this.setState({
        actionInProgress: false,
        unsavedChanges: false,
      });
      this.props.onSavedModification();

      /* Update the default `orderInstructions` to original data */
      this.setState({
        orderInstructions: [...this.state.orderInstructions],
      });
    }
  };

  onSaveDndDraft = async (
    bundle: BundleJson,
    newOrderInstructions: Array<OrderInstruction>
  ) => {
    await this.props.onSavedModification();
    await this.saveActions(bundle, newOrderInstructions);
  };

  render(): JSX.Element {
    const {
      expanded,
      mapOnTop,
      onMapSelected,
      monochrome,
      monitorView,
      bundleDraftIsSelected,
      onExpand,
      onSelectBundleDraft,
      onDeselectBundleDraft,
      completedStatusList,
      openModalToSelectBundle,
      setZonesOfBundles,
    } = this.props;
    const {
      bundle,
      orderInstructions,
      recreateModeActive,
      movingToBundleActive,
      actionInProgress,
      unsavedChanges,
      showChangeShelfModal,
      showTravelModeETAModal,
      selectedOrders,
      isBundleUpdating,
    } = this.state;
    const isDraft = bundle.status === BundleStatus.Draft;
    const pickupsGroup: InstructionGroup =
      this.findPickupInstructionsGroup(orderInstructions);

    return (
      <div
        ref={this.plannerBundleRef}
        key={`planner-bundle-${bundle.id}`}
        className={monitorView ? 'planner-bundle-grid' : 'planner-bundle'}
      >
        {monitorView && isBundleUpdating && (
          <div className="planner-bundle-loader">
            <Loader type="spinner" size="large" />
          </div>
        )}
        <PlannerBundleMenus
          orderInstructions={orderInstructions}
          bundle={bundle}
          mapOnTop={mapOnTop}
          monitorView={monitorView}
          region={this.props.region}
          selectedDate={this.props.selectedDate}
          monochrome={monochrome}
          expanded={expanded}
          recreateModeActive={recreateModeActive}
          movingToBundleActive={movingToBundleActive}
          onMapSelected={onMapSelected}
          onSavedModification={this.props.onSavedModification}
          onExpand={onExpand}
          toggleExpanded={this.toggleExpanded}
          startReordering={this.startReordering}
          stopReordering={this.stopReordering}
          bundleDraftIsSelected={bundleDraftIsSelected}
          onSelectBundleDraft={onSelectBundleDraft}
          onDeselectBundleDraft={onDeselectBundleDraft}
          startRecreating={this.startRecreating}
          stopRecreating={this.stopRecreating}
          completedStatusList={completedStatusList}
          startMoving={this.startMoving}
          stopMoving={this.stopMoving}
          setZonesOfBundles={setZonesOfBundles}
          onOrdersUpdated={this.updateOrders}
        />
        {expanded && (
          <OrderBundle
            border={this.props.useItemBorder}
            defaultOrderInstructions={orderInstructions}
            columns={this.props.itemColumns}
            hasDragAndDrop={
              !completedStatusList &&
              !recreateModeActive &&
              !movingToBundleActive
            }
            hasCheckbox={
              !completedStatusList &&
              (recreateModeActive || movingToBundleActive)
            }
            hasDelete={
              !completedStatusList &&
              !recreateModeActive &&
              !movingToBundleActive
            }
            pickupsGroup={pickupsGroup}
            bundle={bundle}
            onSelectOrderInstruction={this.onSelectOrderInstruction}
            onDeleteOrderInstruction={this.onDeleteOrderInstruction}
            onSaveDndDraft={this.onSaveDndDraft}
          />
        )}
        <div
          className={
            monitorView
              ? 'planner-bundle-instructions-grid'
              : 'planner-bundle-instructions'
          }
        >
          <div className="planner-bundle-instruction-container">
            {expanded && completedStatusList && (
              <div className="planner-bundle-button-group" />
            )}
            {expanded && !completedStatusList && (
              <div className="planner-bundle-button-group">
                {!recreateModeActive &&
                  !movingToBundleActive &&
                  !unsavedChanges && (
                    <>
                      {isDraft && (
                        <button
                          onClick={async () => {
                            const result = await Swal.fire({
                              title: 'Danger zone!',
                              text: `Are you sure you want to delete draft ${bundle.id}?`,
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonText: 'Delete draft',
                              confirmButtonColor: '#d33',
                              cancelButtonColor: '#ccc',
                            });
                            if (result && result.value) {
                              await deleteDraftBundle(bundle.id);
                              await this.props.onSavedModification();
                            }
                          }}
                          disabled={actionInProgress}
                        >
                          Delete
                        </button>
                      )}
                      {!isDraft && (
                        <button
                          onClick={() => this.clickMenuCancelBundle()}
                          disabled={actionInProgress}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        disabled={actionInProgress}
                        onClick={async () => {
                          this.setState({ actionInProgress: true });
                          await reorderDraftNew(
                            bundle.id,
                            pickupOnTop(orderInstructions).map(
                              orderInstruction => orderInstruction.instruction
                            )
                          );
                          await this.props.onSavedModification();
                          this.setState({ actionInProgress: false });
                        }}
                      >
                        Pickup top
                      </button>
                      {!isDraft && (
                        <button
                          onClick={() =>
                            this.setState({ showChangeShelfModal: true })
                          }
                        >
                          Set Shelf
                        </button>
                      )}
                      <button
                        onClick={() =>
                          this.setState({ showTravelModeETAModal: true })
                        }
                        disabled={actionInProgress}
                      >
                        ETA Calc.
                      </button>
                      <button
                        onClick={async e => {
                          this.setState({ actionInProgress: true });
                          await reorderBundleWithAlgorithm(
                            e.shiftKey
                              ? ReorderMode.AutoBundler
                              : ReorderMode.OpenRoute,
                            bundle.id,
                            () => {
                              this.setState({ actionInProgress: false });
                              this.props.onSavedModification();
                            },
                            error => {
                              this.setState({ actionInProgress: false });
                              Swal.fire({
                                title: `Something unexpected occured when re-ordering bundle. Error response: ${JSON.stringify(
                                  error
                                )}`,
                                icon: 'error',
                              });
                            }
                          );
                        }}
                        className={
                          this.state.reorderHoverShift
                            ? 'hover-shift'
                            : undefined
                        }
                        onMouseEnter={e =>
                          e.shiftKey &&
                          this.setState({ reorderHoverShift: true })
                        }
                        onMouseLeave={e =>
                          this.setState({ reorderHoverShift: false })
                        }
                        disabled={actionInProgress}
                      >
                        Reorder
                      </button>
                      <button
                        disabled={actionInProgress}
                        onClick={async () => {
                          this.props.onSavedModification();
                          await this.updateOrders().then(() =>
                            this.saveActions(
                              bundle,
                              reverseDeliveries(orderInstructions)
                            )
                          );
                        }}
                      >
                        Reverse deliveries
                      </button>
                      {isDraft && (
                        <button
                          disabled={actionInProgress}
                          onClick={async () => {
                            const published = await publishDraft(bundle.id);
                            if (published) {
                              await this.props.onSavedModification();
                            } else {
                              alert('Something went wrong');
                            }
                          }}
                        >
                          Publish
                        </button>
                      )}
                    </>
                  )}
                {recreateModeActive && (
                  <>
                    <button
                      onClick={this.stopRecreating}
                      disabled={actionInProgress}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => this.recreateBundle()}
                      disabled={!selectedOrders.length || actionInProgress}
                    >
                      Create new bundle
                    </button>
                  </>
                )}
                {movingToBundleActive && (
                  <>
                    <button
                      onClick={this.stopMoving}
                      disabled={actionInProgress}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => openModalToSelectBundle(bundle.id)}
                      disabled={!selectedOrders.length || actionInProgress}
                    >
                      Move to the bundle
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {showChangeShelfModal && (
          <ChangeShelfModal
            bundle={this.state.bundle}
            onCloseModal={() => {
              this.setState({ showChangeShelfModal: false });
            }}
            onSelectModal={() => {
              this.setState({ showChangeShelfModal: false });
              this.props.onSavedModification();
            }}
          />
        )}
        {showTravelModeETAModal && (
          <ETAOptionsModal
            onCloseModal={() =>
              this.setState({ showTravelModeETAModal: false })
            }
            onTravelModeSelected={async (
              travelMode: TravelMode,
              timePerStop: number
            ) => {
              this.setState({ showTravelModeETAModal: false });
              this.calculateEtaFromAlgorithm(
                bundle.id,
                travelMode,
                timePerStop
              );
            }}
            region={bundle.region}
            predictionModel={this.state.bundle.assignedCourierPredictionModel}
          />
        )}
      </div>
    );
  }
}
